import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Address, erc20Abi, formatUnits } from 'viem'
import {
  useAccount,
  useChainId,
  usePublicClient,
  useReadContracts,
  useSendTransaction,
} from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { CREDIT_TYPES } from '../constants'
import { createSmartFetch } from './useAuthenticatedMutation'
import { useCreditReceiver } from './useCreditReceiver'

export interface ATokenApprovalParams {
  creditType: string
  creditLimit: number // Limit in whole token units (e.g., 1000 = 1000 USDC)
  userParams?: Record<string, any>
  solverParams?: Record<string, any>
}

interface UseATokenApprovalParams {
  tokenAddress?: Address // The aToken or vault address to check allowance for
  requiredLimit?: number // Required limit in whole token units (e.g., 1000 = 1000 USDC)
  creditType?: string
  receiverUserParams?: Record<string, unknown>
}

/**
 * Hook to manage aToken/vault ERC20 approval for card spend mode.
 *
 * The approval flow:
 * 1. User sets their desired spend limit
 * 2. Hook fetches spender address from backend (creditReceiver)
 * 3. Hook checks if current allowance >= required limit
 * 4. If not sufficient, user needs to approve
 * 5. Backend generates approval tx with the correct spender
 * 6. When allowance is consumed, user needs to re-approve
 *
 * IMPORTANT: Both checking AND approving use the same spender address from backend
 * to ensure consistency. The approval is generated by the backend.
 */
export function useATokenApproval({
  tokenAddress,
  requiredLimit = 0,
  creditType = CREDIT_TYPES.HYPURRFI_SPEND,
  receiverUserParams,
}: UseATokenApprovalParams = {}) {
  const { address } = useAccount()
  const chainId = useChainId()
  const queryClient = useQueryClient()
  const publicClient = usePublicClient()

  const { sendTransactionAsync, isPending: isApproving, error: approveError, data: txHash } = useSendTransaction()

  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  // Use shared hook to get credit receiver address from backend
  const { creditReceiver: spenderAddress, isLoading: isLoadingSpender } = useCreditReceiver({
    creditType,
    userParams: {
      aTokenAddress: tokenAddress,
      ...(receiverUserParams ?? {}),
    },
    enabled: !!tokenAddress,
  })

  const resolvedSpenderAddress = spenderAddress
  const isLoadingResolvedSpender = !resolvedSpenderAddress && isLoadingSpender

  // Fetch token decimals and allowance in a single multicall
  const {
    data: tokenData,
    isLoading: isLoadingTokenData,
    isSuccess: isTokenDataLoaded,
    refetch: refetchTokenData,
  } = useReadContracts({
    contracts: [
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'decimals',
      },
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: address && resolvedSpenderAddress ? [address, resolvedSpenderAddress] : undefined,
      },
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'symbol',
      },
    ],
    query: {
      enabled: !!address && !!resolvedSpenderAddress && !!tokenAddress,
      staleTime: 10_000, // Refetch more frequently to catch spent allowance
    },
  })

  // Extract token data
  const tokenDecimals = (tokenData?.[0]?.result as number) ?? 18
  const currentAllowanceRaw = (tokenData?.[1]?.result as bigint) ?? 0n
  const tokenSymbol = (tokenData?.[2]?.result as string) ?? 'TOKEN'

  // Convert raw allowance to token units for comparison
  const currentAllowanceTokens = currentAllowanceRaw ? Number(formatUnits(currentAllowanceRaw, tokenDecimals)) : 0

  // Check if approval is sufficient for the required limit
  // needsApproval = true when current allowance < required limit
  const hasSufficientApproval = isTokenDataLoaded && currentAllowanceTokens >= requiredLimit
  const needsApproval = isTokenDataLoaded && !hasSufficientApproval && requiredLimit > 0

  // Calculate how much allowance remains
  const remainingAllowance = Math.max(0, currentAllowanceTokens)

  // Calculate shortfall (how much more approval is needed)
  const approvalShortfall = Math.max(0, requiredLimit - currentAllowanceTokens)

  // Calculate percentage of allowance remaining (for progress bar)
  const allowancePercentage = requiredLimit > 0 ? Math.min(100, (currentAllowanceTokens / requiredLimit) * 100) : 0

  // Determine if allowance is critically low (below 20%)
  const isCriticallyLow = allowancePercentage < 20 && requiredLimit > 0

  const approveAToken = useMutation({
    mutationFn: async (params: ATokenApprovalParams) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!tokenAddress) {
        throw new Error('Token address not set')
      }

      // Note: Authentication is handled automatically by createSmartFetch via getFreshTokens()
      // which refreshes tokens if needed. No manual auth check required here.

      try {
        // Get approval transaction from backend
        // Backend generates the approval with the correct creditReceiver address
        const delegateFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
          method: 'POST',
          body: JSON.stringify({
            creditType: params.creditType,
            creditLimit: params.creditLimit, // Whole token units - backend multiplies by decimals
            chainID: chainId,
            userParams: {
              ...params.userParams,
              ...(params.solverParams ?? {}),
            },
          }),
        })

        const apiData = await delegateFetch({})
        if (!apiData?.data?.to || !apiData?.data?.callData) {
          throw new Error('Invalid approval response from server')
        }
        const { to, callData, value } = apiData.data

        // Send the transaction from backend (uses correct spender address)
        const hash = await sendTransactionAsync({
          to,
          value: BigInt(value || 0),
          data: callData,
        })

        if (publicClient) {
          await waitForTxReceipt(publicClient, hash)
        }

        // Invalidate queries to refresh allowance
        queryClient.invalidateQueries({
          queryKey: ['readContract'],
        })

        queryClient.invalidateQueries({
          queryKey: ['user-cards', address],
        })

        // Refetch token data immediately
        await refetchTokenData()

        return hash
      } catch (error) {
        // Enhance error messages for user rejections
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (
          errorMessage.includes('rejected') ||
          errorMessage.includes('denied') ||
          errorMessage.includes('cancelled')
        ) {
          throw new Error('User rejected the aToken approval request')
        }
        throw error
      }
    },
  })

  const resetError = () => {
    approveAToken.reset()
  }

  return {
    // Token info
    tokenDecimals,
    tokenSymbol,

    // Allowance info
    currentAllowanceRaw, // Raw allowance from contract
    currentAllowance: currentAllowanceTokens, // Allowance in token units
    remainingAllowance, // How much can still be spent
    requiredLimit, // Required limit in token units

    // Approval status
    needsApproval, // True if allowance < required limit
    hasSufficientApproval, // True if allowance >= required limit
    approvalShortfall, // How much more needs to be approved
    allowancePercentage, // Percentage of allowance remaining (0-100)
    isCriticallyLow, // True if below 20%

    // Loading states
    isLoadingAllowance: isLoadingTokenData || isLoadingResolvedSpender,
    isAllowanceLoaded: isTokenDataLoaded && !isLoadingResolvedSpender,

    // Actions
    approveAToken: approveAToken.mutateAsync,
    refetchAllowance: refetchTokenData,

    // Transaction states
    isApproving, // Transaction being sent
    isConfirming, // Transaction sent, waiting for confirmation
    isConfirmed, // Transaction confirmed
    txHash,
    approveError: approveError || approveAToken.error,
    resetError,

    // Config - spender address from backend
    brahmaAddress: resolvedSpenderAddress,
  }
}
